import type { FormEvent, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send } from "lucide-react";

type ChatComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  submitLabel?: string;
  loadingLabel?: string;
  ariaLabel?: string;
  compact?: boolean;
  alerts?: ReactNode;
  disclaimer?: string;
};

export function ChatComposer({
  value,
  onChange,
  onSubmit,
  placeholder = "Type your message here...",
  disabled = false,
  loading = false,
  submitLabel = "Send",
  loadingLabel = "Waiting",
  ariaLabel = "Message",
  compact = false,
  alerts,
  disclaimer = "Ordinal can make mistakes. Check important info.",
}: ChatComposerProps) {
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (disabled || loading || !value.trim()) return;
    onSubmit();
  };

  const rows = compact ? 3 : 3;
  const textareaClass = compact
    ? "flex-1 min-h-[60px] resize-none text-sm border-none focus-visible:ring-0"
    : "flex-1 min-h-[60px] resize-none border-none focus-visible:ring-0";

  return (
    <div className={compact ? "space-y-2" : "space-y-2"}>
      {alerts}
      <Card className="focus-within:border-primary">
        <form className="flex gap-2 p-2" onSubmit={handleSubmit}>
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={textareaClass}
            rows={rows}
            disabled={disabled || loading}
            aria-label={ariaLabel}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit();
              }
            }}
          />
          <div className="flex flex-row gap-2 items-end">
            <Button type="submit" disabled={disabled || loading || !value.trim()} className={loading ? "cursor-wait" : ""}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                  {loadingLabel}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" aria-hidden />
                  {submitLabel}
                </>
              )}
            </Button>
          </div>
        </form>
      </Card>
      {disclaimer ? <p className="text-xs text-center text-muted-foreground">{disclaimer}</p> : null}
    </div>
  );
}
